/**
 * KPI Period Card
 *
 * Shows KPI goal attainment and total value for a specific period (week/month).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Target, DollarSign, Radio } from 'lucide-react';
import { formatCompactCurrency } from '@/lib/utils';
import type { ScorecardResponse } from '@/features/kpis/types';

interface KpiPeriodCardProps {
  title: string;
  scorecard?: ScorecardResponse;
  periodIndex: number;
  loading?: boolean;
}

function computeMetrics(scorecard: ScorecardResponse, periodIndex: number) {
  let onTrackCount = 0;
  let totalWithValues = 0;
  let dollarSum = 0;

  for (const category of scorecard.categories) {
    for (const kpi of category.kpis) {
      const periodValue = kpi.values[periodIndex];
      if (!periodValue || periodValue.value_count === 0) continue;

      totalWithValues++;
      if (periodValue.on_track === true) onTrackCount++;

      if (kpi.goal_unit === '$' || kpi.goal_unit === 'CAD' || kpi.goal_unit === 'USD') {
        dollarSum += periodValue.value ?? 0;
      }
    }
  }

  const goalPct = totalWithValues > 0
    ? Math.round((onTrackCount / totalWithValues) * 100)
    : null;

  return { goalPct, dollarSum, onTrackCount, totalWithValues };
}

export function KpiPeriodCard({ title, scorecard, periodIndex, loading }: KpiPeriodCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-4 w-24" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-24" />
        </CardContent>
      </Card>
    );
  }

  const metrics = scorecard ? computeMetrics(scorecard, periodIndex) : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-muted-foreground" />
          {metrics?.goalPct !== null && metrics?.goalPct !== undefined ? (
            <span className="text-lg font-bold">
              {metrics.goalPct}%
              <span className="text-xs font-normal text-muted-foreground ml-1">Goals</span>
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">No data</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">
            {metrics ? formatCompactCurrency(metrics.dollarSum) : '$0'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-muted-foreground/40" />
          <span className="text-xs text-muted-foreground/60">Sensors sent — Phase 2</span>
        </div>
      </CardContent>
    </Card>
  );
}
